import { defineStore } from 'pinia'
import { Capacitor } from '@capacitor/core'
import { Preferences } from '@capacitor/preferences'
import { setHouseholdTimeZone } from '@/lib/dates'
import { clearOffline } from '@/lib/offline'
import {
  PasskeyCancelled,
  createPasskey,
  getPasskey,
  hasPasskeyHere,
  markPasskeyHere,
  passkeysSupported,
} from '@/lib/passkeys'
import { ref, computed } from 'vue'
import { api, setAccessToken, setUnauthorizedHandler, ApiError } from '@/api/client'
import type { Me, TokenPair } from '@/api/types'
import type { Passkey, PasskeyOptions } from '@refuelled/shared'

const REFRESH_KEY = 'refuelled.refreshToken'

/**
 * Where the refresh token is kept, and why in two places on a phone.
 *
 * The WebView's localStorage is written to disk lazily, seconds after the
 * call. Refresh rotates the token and the server treats a replaced one,
 * presented again, as stolen (it revokes every session). So when an update
 * killed the app just after a refresh, the next launch read the old token
 * from disk and signed the person out everywhere. On a phone the token also
 * goes to native Preferences, which is written straight away, and is read
 * from there first.
 */
const isNative = () => {
  try {
    return Capacitor.isNativePlatform()
  } catch {
    return false
  }
}

async function readStoredToken(): Promise<string | null> {
  if (isNative()) {
    try {
      const { value } = await Preferences.get({ key: REFRESH_KEY })
      if (value) return value
    } catch {
      // Fall through to the WebView's copy.
    }
  }
  return localStorage.getItem(REFRESH_KEY)
}

/**
 * Written to both, and the native copy waited for: a rotated token that has
 * not reached storage when the app is killed is the one the next launch
 * does not have (ADR-036).
 */
async function writeStoredToken(token: string | null) {
  if (token) localStorage.setItem(REFRESH_KEY, token)
  else localStorage.removeItem(REFRESH_KEY)
  if (isNative()) {
    await (token ? Preferences.set({ key: REFRESH_KEY, value: token }) : Preferences.remove({ key: REFRESH_KEY })).catch(
      () => undefined,
    )
  }
}

/**
 * One refresh at a time across every tab of the app, where the browser can
 * say so (the Web Locks API). Without it, the work just runs.
 */
async function acrossTabs<T>(fn: () => Promise<T>): Promise<T> {
  const locks = typeof navigator === 'undefined' ? undefined : navigator.locks
  if (!locks?.request) return fn()
  return locks.request('refuelled.refresh', fn) as Promise<T>
}

/**
 * How long launch keeps trying when the server cannot be reached, before
 * saying so. An update usually follows a deploy, and the API is down for a
 * few seconds while Jenkins restarts it. Replaceable in tests.
 */
let restoreRetryDelaysMs = [1000, 3000, 8000]
export function setRestoreRetryDelays(delays: number[]) {
  restoreRetryDelaysMs = delays
}

/** The server said no, or could not be asked. Only the first is a reason to sign out. */
type RefreshOutcome = 'ok' | 'rejected' | 'unreachable'

const isRejection = (err: unknown) => err instanceof ApiError && (err.status === 401 || err.status === 403)

export const useAuthStore = defineStore('auth', () => {
  const me = ref<Me | null>(null)
  const loading = ref(false)
  const error = ref<string | null>(null)
  const refreshToken = ref<string | null>(localStorage.getItem(REFRESH_KEY))
  /** Set when the last restore could not reach the server; the token is kept. */
  const unreachable = ref(false)
  /**
   * How the last session ended: the person chose to sign out, or the server
   * ended it. The app goes to the sign-in page either way, and only in the
   * second case remembers the page to come back to.
   */
  const signedOut = ref<'chosen' | 'ended' | null>(null)
  /** Exposed so the socket connection can re-authenticate after a refresh. */
  const accessToken = ref<string | null>(null)

  const isSignedIn = computed(() => me.value !== null)
  const isAdmin = computed(() => me.value?.role === 'admin')

  async function store(tokens: TokenPair) {
    setAccessToken(tokens.accessToken)
    accessToken.value = tokens.accessToken
    refreshToken.value = tokens.refreshToken
    unreachable.value = false
    signedOut.value = null
    await writeStoredToken(tokens.refreshToken)
  }

  async function clear(how: 'chosen' | 'ended' = 'ended') {
    signedOut.value = how
    setAccessToken(null)
    accessToken.value = null
    refreshToken.value = null
    me.value = null
    setHouseholdTimeZone(null)
    await writeStoredToken(null)
    // The shopping list kept for no signal is this person's, not the next one's.
    await clearOffline()
  }

  async function loadMe() {
    me.value = await api.get<Me>('/auth/me')
    setHouseholdTimeZone(me.value.timezone)
  }

  async function signIn(email: string, password: string) {
    loading.value = true
    error.value = null
    try {
      await store(await api.post<TokenPair>('/auth/login', { email, password }))
      await loadMe()
    } catch (err) {
      error.value = err instanceof ApiError ? err.message : 'Could not sign in'
      throw err
    } finally {
      loading.value = false
    }
  }

  /**
   * Sign in with a passkey (S-01, ADR-038): the device offers whichever it
   * holds for Refuelled, and the server gives the same tokens as a password.
   * Closing the sheet is not an error.
   */
  async function signInWithPasskey(): Promise<boolean> {
    loading.value = true
    error.value = null
    try {
      const { challengeId, options } = await api.post<PasskeyOptions>('/auth/passkeys/login/options')
      const response = await getPasskey(options)
      await store(await api.post<TokenPair>('/auth/passkeys/login', { challengeId, response }))
      await loadMe()
      // This device has one, so a password sign-in here later need not offer one.
      if (me.value) markPasskeyHere(me.value.userId)
      return true
    } catch (err) {
      if (err instanceof PasskeyCancelled) return false
      error.value = err instanceof ApiError ? err.message : ((err as Error)?.message ?? 'Could not sign in with a passkey')
      return false
    } finally {
      loading.value = false
    }
  }

  async function listPasskeys(): Promise<Passkey[]> {
    return api.get<Passkey[]>('/auth/passkeys')
  }

  /** Add a passkey for this login. Null when the person closed the sheet. */
  async function addPasskey(name: string): Promise<Passkey | null> {
    const { challengeId, options } = await api.post<PasskeyOptions>('/auth/passkeys/register/options')
    try {
      const response = await createPasskey(options)
      const added = await api.post<Passkey>('/auth/passkeys/register', { challengeId, name, response })
      if (me.value) {
        markPasskeyHere(me.value.userId)
        me.value = { ...me.value, passkeysDeclined: false }
      }
      return added
    } catch (err) {
      if (err instanceof PasskeyCancelled) return null
      throw err
    }
  }

  /**
   * Whether to offer a passkey just after a password sign-in: the device can
   * make one, this login has not made one here, and they have not said they
   * would rather keep their password.
   */
  async function shouldOfferPasskey(): Promise<boolean> {
    if (!me.value || me.value.passkeysDeclined || hasPasskeyHere(me.value.userId)) return false
    return passkeysSupported()
  }

  /** "I'll stick with passwords", for this login on every device. */
  async function declinePasskeys(): Promise<void> {
    await api.post('/auth/passkeys/decline')
    if (me.value) me.value = { ...me.value, passkeysDeclined: true }
  }

  // ------------------------------------------------ connected apps (ADR-040) --

  /** Which app is asking to connect, checked against where it said it would come back to. */
  async function oauthClient(clientId: string, redirectUri: string) {
    return api.get<{ clientId: string; name: string; redirectHost: string }>(
      `/oauth/clients/${encodeURIComponent(clientId)}`,
      { redirect_uri: redirectUri },
    )
  }

  /** The answer on the consent page; the browser then goes where this says. */
  async function decideOAuth(input: {
    decision: 'allow' | 'deny'
    clientId: string
    redirectUri: string
    codeChallenge: string
    codeChallengeMethod: string
    state?: string | null
    scope?: string | null
    resource?: string | null
  }) {
    return api.post<{ redirectTo: string }>('/oauth/authorize', input)
  }

  async function connectedApps() {
    return api.get<Array<{ id: string; name: string; createdAt: string; lastUsedAt: string | null }>>('/oauth/grants')
  }

  async function disconnectApp(id: string) {
    await api.delete(`/oauth/grants/${id}`)
  }

  async function removePasskey(id: string): Promise<void> {
    await api.delete(`/auth/passkeys/${id}`)
  }

  async function signUp(input: {
    email: string
    password: string
    householdName: string
    displayName: string
  }) {
    loading.value = true
    error.value = null
    try {
      await api.post('/auth/signup', input)
    } catch (err) {
      error.value = err instanceof ApiError ? err.message : 'Could not create the account'
      throw err
    } finally {
      loading.value = false
    }
  }

  /**
   * One refresh at a time, however many requests asked for it.
   *
   * The server rotates: refreshing revokes the token presented and issues a
   * new one, and presenting a revoked token is treated as theft and revokes
   * every session that person has. So several requests 401ing together, which
   * is exactly what a screen making three calls does the moment an access
   * token runs out, must not each post the same token. The first would
   * succeed and the rest would look like a replay.
   */
  let inFlight: Promise<RefreshOutcome> | null = null

  function refreshOnce(): Promise<RefreshOutcome> {
    if (!refreshToken.value) return Promise.resolve('rejected')
    inFlight ??= exchangeRefreshToken().finally(() => {
      inFlight = null
    })
    return inFlight
  }

  /** Swap the stored refresh token for a fresh pair. Returns false if it fails. */
  async function tryRefresh(): Promise<boolean> {
    return (await refreshOnce()) === 'ok'
  }

  /**
   * Only the server saying no signs anybody out. A network error, a 5xx
   * while a deploy restarts the API, or being rate limited keeps the token
   * for the next try: it used to be cleared on any failure, so opening the
   * app during a deploy, or with no signal, signed the person out.
   */
  function exchangeRefreshToken(): Promise<RefreshOutcome> {
    return acrossTabs(async () => {
      // Another tab may have rotated the token since this one read it, and
      // the one held here would then be a replaced token (ADR-036).
      const stored = await readStoredToken()
      if (stored) refreshToken.value = stored
      try {
        // `isRefresh` keeps a 401 from this call out of the handler that made
        // it. Without it the rejection recurses instead of being caught here.
        await store(
          await api.post<TokenPair>(
            '/auth/refresh',
            { refreshToken: refreshToken.value },
            { isRefresh: true },
          ),
        )
        return 'ok'
      } catch (err) {
        if (isRejection(err)) {
          await clear()
          return 'rejected'
        }
        return 'unreachable'
      }
    })
  }

  /**
   * Restore a session on page load, before the router decides where to go.
   * Tries again for a few seconds when the server cannot be reached; if it
   * still cannot, keeps the token and says so (`unreachable`) rather than
   * signing out.
   */
  async function restore(): Promise<boolean> {
    const stored = await readStoredToken()
    if (stored) refreshToken.value = stored
    if (!refreshToken.value) return false
    for (let attempt = 0; ; attempt += 1) {
      const outcome = await refreshOnce()
      if (outcome === 'ok') break
      if (outcome === 'rejected') return false
      if (attempt >= restoreRetryDelaysMs.length) {
        unreachable.value = true
        return false
      }
      await new Promise((resolve) => setTimeout(resolve, restoreRetryDelaysMs[attempt]))
    }
    try {
      await loadMe()
      return true
    } catch (err) {
      if (isRejection(err)) await clear()
      else unreachable.value = true
      return false
    }
  }

  /** Tokens handed back by a sign-in that happened elsewhere (Google). */
  async function adoptTokens(tokens: TokenPair) {
    await store(tokens)
    await loadMe()
  }

  /** Change my own password; the session stays as it is. */
  async function changePassword(currentPassword: string, newPassword: string) {
    await api.post('/auth/change-password', { currentPassword, newPassword })
  }

  async function signOut() {
    try {
      await api.post('/auth/logout')
    } catch {
      // Signing out locally matters more than the server acknowledging it.
    }
    await clear('chosen')
  }

  setUnauthorizedHandler(tryRefresh)

  return {
    me,
    loading,
    error,
    accessToken,
    unreachable,
    signedOut,
    isSignedIn,
    isAdmin,
    signIn,
    signInWithPasskey,
    listPasskeys,
    addPasskey,
    removePasskey,
    shouldOfferPasskey,
    oauthClient,
    decideOAuth,
    connectedApps,
    disconnectApp,
    declinePasskeys,
    signUp,
    signOut,
    restore,
    tryRefresh,
    changePassword,
    adoptTokens,
    loadMe,
  }
})
